import React, { useEffect, useState } from "react";
import { DatePicker, Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { createAttendance, updateAttendance } from "../../apis/attendance";
import { useNotification } from "../../NotificationContext";
import { getClinicUsers } from "../../apis/user";

const STATUS_OPTIONS = [
  { value: "present",  label: "Present" },
  { value: "late",     label: "Late" },
  { value: "absent",   label: "Absent" },
  { value: "half_day", label: "Half Day" },
  { value: "leave",    label: "Leave" },
  { value: "holiday",  label: "Holiday" },
];

interface Props {
  record: any | null; // null = new manual entry
  onClose: () => void;
  onSaved: () => void;
}

const AttendanceOverrideModal: React.FC<Props> = ({ record, onClose, onSaved }) => {
  const { showNotification } = useNotification();
  const [clinicUsers, setClinicUsers] = useState<any[]>([]);
  const [clinicUserId, setClinicUserId] = useState<number | null>(null);
  const [attendanceDate, setAttendanceDate] = useState<string>("");
  const [checkIn, setCheckIn] = useState<dayjs.Dayjs | null>(null);
  const [checkOut, setCheckOut] = useState<dayjs.Dayjs | null>(null);
  const [status, setStatus] = useState("present");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const isEdit = !!record;

  useEffect(() => {
    getClinicUsers().then((res: any) => {
      const users = res.data?.data || res.data || [];
      setClinicUsers(users);
    });
  }, []);

  useEffect(() => {
    if (record) {
      setClinicUserId(record.clinicUserId);
      setAttendanceDate(record.attendanceDate);
      setCheckIn(record.checkInTime ? dayjs(record.checkInTime) : null);
      setCheckOut(record.checkOutTime ? dayjs(record.checkOutTime) : null);
      setStatus(record.status || "present");
      setReason(record.overrideReason || "");
    }
  }, [record]);

  const handleSave = async () => {
    if (!isEdit && (!clinicUserId || !attendanceDate)) {
      showNotification("error", "Employee and date are required", "", "topRight");
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        status,
        checkInTime:  checkIn  ? checkIn.format("YYYY-MM-DD HH:mm:ss")  : null,
        checkOutTime: checkOut ? checkOut.format("YYYY-MM-DD HH:mm:ss") : null,
        overrideReason: reason || null,
      };

      if (!isEdit) {
        payload.clinicUserId  = clinicUserId;
        payload.attendanceDate = attendanceDate;
        await createAttendance(payload);
      } else {
        await updateAttendance(record.id, payload);
      }

      showNotification("success", isEdit ? "Attendance updated" : "Attendance entry added", "", "topRight");
      onSaved();
    } catch (err: any) {
      showNotification("error", err?.response?.data?.message || "Failed to save", "", "topRight");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {!isEdit && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <Select
              showSearch
              placeholder="Select employee"
              className="w-full"
              value={clinicUserId ?? undefined}
              onChange={(v) => setClinicUserId(v)}
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={clinicUsers.map((u: any) => ({
                value: u.id,
                label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.name || `User ${u.id}`,
              }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <DatePicker
              className="w-full"
              value={attendanceDate ? dayjs(attendanceDate) : null}
              onChange={(d) => setAttendanceDate(d ? d.format("YYYY-MM-DD") : "")}
            />
          </div>
        </>
      )}

      {isEdit && (
        <div className="text-sm text-gray-500 bg-gray-50 rounded p-2">
          {record?.userName} &mdash; {record?.attendanceDate}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <Select
          className="w-full"
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
          <TimePicker
            className="w-full"
            value={checkIn}
            onChange={setCheckIn}
            format="HH:mm"
            minuteStep={5}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
          <TimePicker
            className="w-full"
            value={checkOut}
            onChange={setCheckOut}
            format="HH:mm"
            minuteStep={5}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Note</label>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
          placeholder="Optional note..."
        />
      </div>

      <div className="flex gap-3 pt-2 border-t border-gray-100">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={onClose}
          className="text-sm font-semibold text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AttendanceOverrideModal;
